/** Telegram-style text bubble: max 4096 UTF-16 code units (string.length). */

export const TEXT_BUBBLE_MAX_CHARS = 4096;
/** After this many bubbles TG sends Message.txt instead. */
export const TEXT_BUBBLE_MAX_PARTS = 8;
export const TEXT_AS_FILE_MIN_CHARS =
  TEXT_BUBBLE_MAX_CHARS * TEXT_BUBBLE_MAX_PARTS;
export const MESSAGE_TXT_NAME = "Message.txt";

export function shouldSplitMessageType(type?: string | null): boolean {
  const t = String(type || "");
  return t === "text" || t === "link" || t === "story_reply";
}

export function shouldSendTextAsFile(text?: string | null): boolean {
  return String(text ?? "").length >= TEXT_AS_FILE_MIN_CHARS;
}

export function makeMessageTxtFile(
  text: string,
  fileName: string = MESSAGE_TXT_NAME,
): File {
  const body = String(text ?? "");
  let name = String(fileName || MESSAGE_TXT_NAME)
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "_");
  if (!name) name = MESSAGE_TXT_NAME;
  if (!/\.txt$/i.test(name)) name = `${name}.txt`;
  return new File([body], name, {
    type: "text/plain;charset=utf-8",
  });
}

/**
 * Split without breaking surrogate pairs.
 * Prefer paragraph → newline → whitespace → hard cut.
 */
export function splitTextIntoBubbles(
  text: string,
  maxLen = TEXT_BUBBLE_MAX_CHARS,
): string[] {
  const raw = text == null ? "" : String(text);
  const limit = Number(maxLen);
  if (!(limit > 0)) return raw ? [raw] : [];
  if (raw.length <= limit) return raw ? [raw] : [];

  const chunks: string[] = [];
  let rest = raw;

  while (rest.length > limit) {
    let cut = findSplitIndex(rest, limit);
    if (cut > 0 && isHighSurrogate(rest.charCodeAt(cut - 1))) {
      cut -= 1;
    }
    if (cut <= 0) cut = limit;
    if (cut > 0 && isHighSurrogate(rest.charCodeAt(cut - 1))) {
      cut = Math.min(
        rest.length,
        limit + (isLowSurrogate(rest.charCodeAt(limit)) ? 1 : 0),
      );
      if (cut > 0 && isHighSurrogate(rest.charCodeAt(cut - 1))) cut -= 1;
      if (cut <= 0) cut = Math.min(rest.length, 2);
    }

    const piece = rest.slice(0, cut);
    if (piece.length) chunks.push(piece);
    rest = rest.slice(cut);
    if (rest.startsWith("\n")) rest = rest.slice(1);
    else if (rest.startsWith("\r\n")) rest = rest.slice(2);
  }
  if (rest.length) chunks.push(rest);
  return chunks.filter((c) => c.length > 0);
}

function findSplitIndex(s: string, limit: number): number {
  const window = s.slice(0, limit);
  let idx = window.lastIndexOf("\n\n");
  if (idx >= Math.floor(limit * 0.5)) return idx + 2;
  idx = window.lastIndexOf("\n");
  if (idx >= Math.floor(limit * 0.5)) return idx + 1;
  idx = Math.max(window.lastIndexOf(" "), window.lastIndexOf("\t"));
  if (idx >= Math.floor(limit * 0.5)) return idx + 1;
  return limit;
}

function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

function isLowSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff;
}

/** Normalize send-message ack: prefer data.messages[] when server split. */
export function messagesFromSendAck(data: unknown): unknown[] {
  if (!data || typeof data !== "object") return [];
  const obj = data as { messages?: unknown; message_id?: unknown };
  if (Array.isArray(obj.messages) && obj.messages.length > 0) {
    return obj.messages;
  }
  if (obj.message_id != null) return [data];
  return [];
}
